/**
 * Bank-related pure utilities (no UI, no feature dependencies).
 *
 * Keep this file free of component/form concerns.
 */

import { z } from 'zod';

/**
 * IBAN validation regex pattern
 * Format: 2 uppercase letters (country code) + 2 digits (check digits) + 11-30 alphanumeric characters
 */
export const IBAN_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;

/**
 * BIC validation regex pattern
 * Format: 6 uppercase letters + 2 alphanumeric characters + optional 3 alphanumeric characters
 * Total length: 8-11 characters
 */
export const BIC_REGEX = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

/**
 * SEPA character set validation regex pattern
 * Allows: A-Z, a-z, 0-9, space, and special characters: / - ? : ( ) . , ' +
 * Based on ISO 20022 character set for payment purposes
 */
export const SEPA_CHARACTER_SET_REGEX = /^[A-Za-z0-9\s\/\-\?\:\(\)\.\,\'\+]*$/;

/**
 * Banking Circle character set validation regex pattern
 * Allows the SEPA set plus underscore: A-Z, a-z, 0-9, space, / - ? : ( ) . , ' + _
 * Used to guard fields sent to Banking Circle (names, address, payment reference).
 */
export const BANKING_CIRCLE_CHARACTER_SET_REGEX = /^[A-Za-z0-9\s\/\-\?\:\(\)\.\,\'\+_]*$/;

/**
 * Payment reference character set.
 * Allows ASCII letters, digits, spaces, and / - ? : ( ) . , ' +.
 */
export const PAYMENT_REFERENCE_CHARACTER_SET_REGEX = /^[A-Za-z0-9 \/\-\?\:\(\)\.\,\'\+]*$/;

/**
 * Maximum length for free-text fields sent to Banking Circle.
 */
export const BANKING_CIRCLE_TEXT_MAX_LENGTH = 35;

/**
 * Replaces ECMAScript line terminators with spaces in payment reference text.
 */
export const stripLineBreaks = (value: string): string =>
  value.replace(/\r\n|[\r\n\u2028\u2029]/g, ' ');

/**
 * Banking Circle fields cannot start with ':' or '-'.
 */
const BANKING_CIRCLE_LEADING_CHAR_REGEX = /^[:-]/;

/**
 * Normalizes IBAN by removing spaces and converting to uppercase
 * @param iban - The IBAN string to normalize
 * @returns Normalized IBAN string
 */
export const normalizeIban = (iban: string): string => {
  return iban.replace(/\s+/g, '').toUpperCase();
};

const groupIbanInFours = (value: string) => value.match(/.{1,4}/g)?.join(' ') ?? value;

export const formatIban = (iban: string): string => {
  const normalized = normalizeIban(iban);
  return groupIbanInFours(normalized);
};

/**
 * Validates IBAN format using regex
 * @param iban - The IBAN string to validate (can contain spaces)
 * @returns true if IBAN format is valid, false otherwise
 */
export const validateIban = (iban: string): boolean => {
  const normalized = normalizeIban(iban);
  return IBAN_REGEX.test(normalized);
};

/**
 * Normalizes BIC by removing spaces and converting to uppercase
 * @param bic - The BIC string to normalize
 * @returns Normalized BIC string
 */
export const normalizeBic = (bic: string): string => {
  return bic.replace(/\s+/g, '').toUpperCase();
};

/**
 * Validates BIC format using regex and length
 * @param bic - The BIC string to validate (can contain spaces)
 * @returns true if BIC format is valid, false otherwise
 */
export const validateBic = (bic: string): boolean => {
  const normalized = normalizeBic(bic);
  // BIC must be between 8 and 11 characters
  if (normalized.length < 8 || normalized.length > 11) {
    return false;
  }
  return BIC_REGEX.test(normalized);
};

/**
 * Creates a Zod schema for IBAN validation
 * Uses the same validation logic as validateIban function
 * @param options - Optional configuration
 * @param options.required - Whether IBAN is required (default: true)
 * @param options.errorMessage - Custom error message (default: 'Please enter a valid IBAN.')
 * @returns Zod string schema with IBAN validation
 */
export const createIbanSchema = (options?: { required?: boolean; errorMessage?: string }) => {
  const { required = true, errorMessage = 'Please enter a valid IBAN.' } = options || {};

  if (!required) {
    return z
      .string()
      .max(34, 'Maximum 34 characters allowed.')
      .refine((value) => value.length === 0 || validateIban(value), { message: errorMessage });
  }

  return z
    .string()
    .min(1, errorMessage)
    .min(15, errorMessage)
    .max(34, 'Maximum 34 characters allowed.')
    .refine((val) => validateIban(val), { message: errorMessage });
};

/**
 * Creates a Zod schema for BIC validation
 * Uses the same validation logic as validateBic function
 * @param options - Optional configuration
 * @param options.required - Whether BIC is required (default: false)
 * @param options.errorMessage - Custom error message (default: 'Please enter a valid BIC.')
 * @returns Zod string schema with BIC validation
 */
export const createBicSchema = (options?: { required?: boolean; errorMessage?: string }) => {
  const { required = false, errorMessage = 'Please enter a valid BIC.' } = options || {};

  if (required) {
    return z
      .string()
      .min(8, errorMessage)
      .max(11, 'Maximum 11 characters allowed.')
      .refine((val) => validateBic(val), { message: errorMessage });
  }

  return z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 8, {
      message: errorMessage,
    })
    .refine((val) => !val || val.length <= 11, {
      message: 'Maximum 11 characters allowed.',
    })
    .refine((val) => !val || validateBic(val), { message: errorMessage });
};

/**
 * Creates a Zod schema for SWIFT code validation
 * SWIFT codes have the same format as BIC codes (8-11 alphanumeric characters)
 * Uses the same validation logic as validateBic function
 * @param options - Optional configuration
 * @param options.required - Whether SWIFT code is required (default: false)
 * @param options.errorMessage - Custom error message (default: 'Please enter a valid SWIFT code.')
 * @returns Zod string schema with SWIFT code validation
 */
export const createSwiftCodeSchema = (options?: { required?: boolean; errorMessage?: string }) => {
  const { required = false, errorMessage = 'Please enter a valid SWIFT code.' } = options || {};

  if (required) {
    return z
      .string()
      .min(8, errorMessage)
      .max(11, 'Maximum 11 characters allowed.')
      .refine((val) => validateBic(val), { message: errorMessage });
  }

  return z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 8, {
      message: errorMessage,
    })
    .refine((val) => !val || val.length <= 11, {
      message: 'Maximum 11 characters allowed.',
    })
    .refine((val) => !val || validateBic(val), { message: errorMessage });
};

/**
 * Validates if a string contains only SEPA character set characters
 * @param value - The string to validate
 * @returns true if the string contains only SEPA characters, false otherwise
 */
export const validateSepaCharacterSet = (value: string): boolean => {
  return SEPA_CHARACTER_SET_REGEX.test(value);
};

export interface CreateSepaTextSchemaOptions {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  requiredErrorMessage?: string;
  maxLengthErrorMessage?: string;
  invalidCharactersErrorMessage?: string;
}

/**
 * SEPA text schema factory.
 *
 * - Accepts `string | null | undefined` input (helps with PrimeVue Forms)
 * - Normalizes to trimmed string output
 * - Applies min/max length rules
 * - Validates allowed SEPA character set
 */
export const createSepaTextSchema = (options?: CreateSepaTextSchemaOptions) => {
  const {
    required = false,
    minLength = 1,
    maxLength = 150,
    requiredErrorMessage = 'This field is required.',
    maxLengthErrorMessage = `Maximum ${maxLength} characters allowed.`,
    invalidCharactersErrorMessage = 'Invalid characters used.',
  } = options || {};

  const nullableTrimmedString = () =>
    z
      .union([z.string(), z.null(), z.undefined()])
      .transform((value): string => (value ?? '').trim());

  const base = nullableTrimmedString().pipe(z.string().max(maxLength, maxLengthErrorMessage));

  if (required) {
    return base
      .pipe(z.string().min(minLength, requiredErrorMessage))
      .refine((val) => validateSepaCharacterSet(val), {
        message: invalidCharactersErrorMessage,
      });
  }

  // Optional: empty string is allowed; min length applies only when non-empty.
  return base
    .refine((val: string) => val === '' || val.length >= minLength, {
      message: requiredErrorMessage,
    })
    .refine((val: string) => val === '' || validateSepaCharacterSet(val), {
      message: invalidCharactersErrorMessage,
    });
};

/**
 * Creates a Zod schema for recipient name validation with SEPA character set
 * @param options - Optional configuration
 * @param options.required - Whether the field is required (default: true)
 * @param options.minLength - Minimum length (default: 1)
 * @param options.maxLength - Maximum length (default: 150)
 * @param options.requiredErrorMessage - Error message for required/too short (default: "Please enter the recipient's full name.")
 * @param options.maxLengthErrorMessage - Error message for too long (default: "Maximum 150 characters allowed.")
 * @param options.invalidCharactersErrorMessage - Error message for invalid characters (default: "Invalid characters used.")
 * @returns Zod string schema with SEPA validation
 */
export const createRecipientNameSchema = (options?: {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  requiredErrorMessage?: string;
  maxLengthErrorMessage?: string;
  invalidCharactersErrorMessage?: string;
}) => {
  const {
    required = true,
    minLength = 1,
    maxLength = 150,
    requiredErrorMessage = "Please enter the recipient's full name.",
    maxLengthErrorMessage = 'Maximum 150 characters allowed.',
    invalidCharactersErrorMessage = 'Invalid characters used.',
  } = options || {};

  return createSepaTextSchema({
    required,
    minLength,
    maxLength,
    requiredErrorMessage,
    maxLengthErrorMessage,
    invalidCharactersErrorMessage,
  });
};

/**
 * Validates a value against the Banking Circle character set.
 * Rejects unsupported characters and values starting with ':' or '-'.
 */
export const validateBankingCircleCharacterSet = (value: string): boolean =>
  BANKING_CIRCLE_CHARACTER_SET_REGEX.test(value) && !BANKING_CIRCLE_LEADING_CHAR_REGEX.test(value);

export const validatePaymentReferenceCharacterSet = (value: string): boolean =>
  PAYMENT_REFERENCE_CHARACTER_SET_REGEX.test(value) &&
  !BANKING_CIRCLE_LEADING_CHAR_REGEX.test(value);

export interface CreateBankingCircleTextSchemaOptions {
  required?: boolean;
  maxLength?: number;
  requiredErrorMessage?: string;
  maxLengthErrorMessage?: string;
  invalidCharactersErrorMessage?: string;
}

/**
 * Banking Circle text schema factory for fields sent to Banking Circle.
 *
 * - Accepts `string | null | undefined` input (helps with PrimeVue Forms)
 * - Normalizes to a trimmed string output (covers the "only blank spaces" rule)
 * - Enforces the max length (default 35)
 * - Validates the Banking Circle character set, including the leading ':' / '-' rule
 */
export const createBankingCircleTextSchema = (options?: CreateBankingCircleTextSchemaOptions) => {
  const {
    required = false,
    maxLength = BANKING_CIRCLE_TEXT_MAX_LENGTH,
    requiredErrorMessage = 'beneficiary_bank_form.validation.empty',
    maxLengthErrorMessage = 'beneficiary_bank_form.validation.max_length_35',
    invalidCharactersErrorMessage = 'beneficiary_bank_form.validation.unsupported_characters',
  } = options || {};

  const nullableTrimmedString = () =>
    z
      .union([z.string(), z.null(), z.undefined()])
      .transform((value): string => stripLineBreaks(value ?? '').trim());

  const base = nullableTrimmedString().pipe(z.string().max(maxLength, maxLengthErrorMessage));

  if (required) {
    return base
      .pipe(z.string().min(1, requiredErrorMessage))
      .refine((val) => validateBankingCircleCharacterSet(val), {
        message: invalidCharactersErrorMessage,
      });
  }

  return base
    .default('')
    .refine((val: string) => val === '' || validateBankingCircleCharacterSet(val), {
      message: invalidCharactersErrorMessage,
    });
};

export interface CreatePaymentReferenceSchemaOptions {
  maxLength?: number;
  requiredErrorMessage?: string;
  maxLengthErrorMessage?: string;
  invalidCharactersErrorMessage?: string;
}

/**
 * Payment reference schema.
 *
 * - Replaces line terminators with spaces
 * - Enforces the payment reference character set and leading ':' / '-' rule
 * - Requires a non-empty trimmed value
 */
export const createPaymentReferenceSchema = (options?: CreatePaymentReferenceSchemaOptions) => {
  const {
    maxLength = 140,
    requiredErrorMessage = 'This field is required.',
    maxLengthErrorMessage = `Maximum ${maxLength} characters allowed.`,
    invalidCharactersErrorMessage = 'Invalid characters used.',
  } = options || {};

  return z
    .union([z.string(), z.null(), z.undefined()])
    .transform((value): string => stripLineBreaks(value ?? '').trim())
    .pipe(z.string().max(maxLength, maxLengthErrorMessage))
    .pipe(z.string().min(1, requiredErrorMessage))
    .refine((value) => validatePaymentReferenceCharacterSet(value), {
      message: invalidCharactersErrorMessage,
    });
};
